// src/api/ticketingApi.ts
import api from "./axios";

export type TicketCreateResponse = {
  id?: string;
  ticketId?: string;
  provider?: string;
  status?: string;
  createdAt?: string;
  dedup?: boolean;
};

function p(path: string) {
  return path.startsWith("/") ? path : `/${path}`;
}

export async function createTeacherDelay(
  reservaId: number,
  minutesLate: number,
  comment?: string,
  files?: File[]
): Promise<TicketCreateResponse> {
  const form = new FormData();
  form.append("minutesLate", String(minutesLate));
  if (comment) form.append("comment", comment);
  (files ?? []).forEach((f) => form.append("attachments", f));
  const { data } = await api.post<TicketCreateResponse>(
    p(`reservas/${reservaId}/incidencias/teacher-delay`),
    form
  );
  return data;
}

export async function createStudentDelay(
  reservaId: number,
  studentId: number,
  minutesLate: number,
  comment?: string,
  files?: File[]
): Promise<TicketCreateResponse> {
  const form = new FormData();
  form.append("studentId", String(studentId));
  form.append("minutesLate", String(minutesLate));
  if (comment) form.append("comment", comment);
  (files ?? []).forEach((f) => form.append("attachments", f));
  const { data } = await api.post<TicketCreateResponse>(
    p(`reservas/${reservaId}/incidencias/student-delay`),
    form
  );
  return data;
}

export async function createStudentAbsence(
  reservaId: number,
  studentId: number,
  comment?: string,
  files?: File[]
): Promise<TicketCreateResponse> {
  const form = new FormData();
  form.append("studentId", String(studentId));
  if (comment) form.append("comment", comment);
  (files ?? []).forEach((f) => form.append("attachments", f));
  const { data } = await api.post<TicketCreateResponse>(
    p(`reservas/${reservaId}/incidencias/student-absence`),
    form
  );
  return data;
}

export async function createTeacherAbsence(
  reservaId: number,
  comment?: string,
  files?: File[]
): Promise<TicketCreateResponse> {
  const form = new FormData();
  if (comment) form.append("comment", comment);
  (files ?? []).forEach((f) => form.append("attachments", f));
  const { data } = await api.post<TicketCreateResponse>(
    p(`reservas/${reservaId}/incidencias/teacher-absence`),
    form
  );
  return data;
}

export async function fetchReservaStudentIds(reservaId: number): Promise<number[]> {
  const { data } = await api.get<number[]>(p(`reservas/${reservaId}/alumnos-ids`));
  return data ?? [];
}

export type AvailabilityChangePayload = {
  type: "permanente" | "temporal" | "vacaciones" | "baja_medica" | "otro";
  effectiveFrom: string;
  effectiveTo?: string;
  timezone?: string;
  reasonCode?: string;
  weekly?: { weekday: number; ranges: { start: string; end: string }[] }[];
  comment?: string;
  files?: File[];
};

export async function createAvailabilityChange(payload: AvailabilityChangePayload) {
  const form = new FormData();
  form.append("type", payload.type);
  form.append("effectiveFrom", payload.effectiveFrom);
  if (payload.effectiveTo) form.append("effectiveTo", payload.effectiveTo);
  if (payload.timezone) form.append("timezone", payload.timezone);
  if (payload.reasonCode) form.append("reasonCode", payload.reasonCode);

  // ⬅️ importante: enviamos siempre weekly (aunque sea [])
  // Debug: mostrar en consola el contenido que vamos a enviar para 'weekly'
  if (typeof window !== 'undefined' && (console && console.debug)) {
    console.debug('[ticketingApi] createAvailabilityChange - weekly payload:', payload.weekly);
  }
  form.append("weekly", JSON.stringify(payload.weekly ?? []));

  if (payload.comment) form.append("comment", payload.comment);
  (payload.files ?? []).forEach((f) => form.append("attachments", f));

  // 🔎 DEBUG: listar exactamente lo que sale en el FormData (especialmente weekly)
  if (typeof window !== "undefined") {
    // FormData preparado
  }

  const { data } = await api.post<TicketCreateResponse>(p("disponibilidad-profesor/cambios"), form);
  return data;
}

export type HrInquiryPayload = {
  type: "duda" | "queja" | "solicitud";
  comment: string;
  requesterEmail?: string;
  files?: File[];
};

export async function createHrInquiry(payload: HrInquiryPayload): Promise<TicketCreateResponse> {
  const form = new FormData();
  form.append("type", payload.type);
  form.append("comment", payload.comment);
  if (payload.requesterEmail) form.append("requesterEmail", payload.requesterEmail);
  (payload.files ?? []).forEach((f) => form.append("files", f));

  // ✅ Usamos la instancia axios 'api' (apunta a tu backend:3001) y el mismo helper p()
  const { data } = await api.post<TicketCreateResponse>(p("profesores/hr-ticket"), form);
  return data;
}

export async function createStaffDelay(
  reservaId: number,
  minutesLate: number,
  comment?: string,
  files?: File[]
): Promise<TicketCreateResponse> {
  const form = new FormData();
  form.append("minutesLate", String(minutesLate));
  if (comment) form.append("comment", comment);
  (files ?? []).forEach((f) => form.append("attachments", f));
  const { data } = await api.post<TicketCreateResponse>(
    p(`reservas/${reservaId}/incidencias/staff-delay`),
    form
  );
  return data;
}
